import React, { useState } from 'react';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Shield, 
  Building, 
  Edit3, 
  Save, 
  X,
  Camera,
  Award,
  Calendar,
  FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import ApiService from '../../services/api';
import ProfilePictureUpload from './ProfilePictureUpload';

const ProfilePage = () => {
  const { user: dataUser } = useData();
  const { user: authUser, updateUser } = useAuth();
  const user = authUser || dataUser; // Prefer auth user since it has the most recent updates
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    address: user?.address || {
      street: '',
      city: '',
      state: '',
      zip: ''
    },
    profile_image: user?.profile_image || ''
  });

  const handleProfileImageUpdate = async (imageBase64) => {
    try {
      console.log('Updating profile image for user:', user.id);
      console.log('Image data length:', imageBase64?.length);
      
      const updatedUser = await ApiService.updateUser(user.id, { 
        profile_image: imageBase64 
      });
      
      console.log('API response:', updatedUser);
      updateUser(updatedUser);
      
      // Update the form data as well to keep it in sync
      setFormData(prev => ({
        ...prev,
        profile_image: imageBase64
      }));
      
      alert('Profile image updated successfully!');
    } catch (error) {
      console.error('Error updating profile image:', error);
      alert('Failed to update profile image. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const updatedUser = await ApiService.updateUser(user.id, formData);
      updateUser(updatedUser);
      setEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      phone: user?.phone || '',
      address: user?.address || {
        street: '',
        city: '',
        state: '',
        zip: ''
      },
      profile_image: user?.profile_image || ''
    });
    setEditing(false);
  };

  const getBranchColor = (branch) => {
    const colors = {
      'Army': 'bg-green-600',
      'Navy': 'bg-blue-600',
      'Air Force': 'bg-sky-600',
      'Marines': 'bg-red-600',
      'Coast Guard': 'bg-orange-600',
      'Space Force': 'bg-purple-600'
    };
    return colors[branch] || 'bg-gray-600';
  };

  const states = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];

  if (!user) {
    return (
      <div className="p-6 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Profile Header */}
        <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-xl">
          <CardContent className="p-8">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-6">
                {editing ? (
                  <ProfilePictureUpload 
                    currentImage={user.profile_image}
                    onImageUpdate={handleProfileImageUpdate}
                    className="flex-shrink-0"
                  />
                ) : (
                  <div className="relative">
                    <Avatar className="w-24 h-24 border-4 border-white shadow-lg">
                      <AvatarImage src={user.profile_image} alt={user.name} />
                      <AvatarFallback className={`${getBranchColor(user.branch)} text-white text-2xl`}>
                        {user.name?.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    {/* Small edit icon to make it clear this can be edited */}
                    <div className="absolute bottom-0 right-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center">
                      <Camera className="w-3 h-3" />
                    </div>
                  </div>
                )}
                
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{user.name}</h1>
                  <div className="flex items-center gap-3 mt-2">
                    <Badge className={`${getBranchColor(user.branch)} text-white`}>
                      {user.rank} • {user.branch}
                    </Badge>
                    <Badge variant="outline" className="border-gray-300">
                      {user.status}
                    </Badge>
                    {user.clearance_level && (
                      <Badge variant="outline" className="border-yellow-400 text-yellow-700 bg-yellow-50">
                        <Shield className="w-3 h-3 mr-1" />
                        {user.clearance_level}
                      </Badge>
                    )}
                  </div>
                  <p className="text-gray-600 mt-2">Military ID: {user.military_id}</p>
                  <p className="text-gray-600">Service #: {user.service_number}</p>
                  {user.unit && <p className="text-gray-600">{user.unit}</p>}
                </div>
              </div>
              
              <div className="flex gap-2">
                {editing ? (
                  <>
                    <Button 
                      onClick={handleSave}
                      disabled={loading}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </Button>
                    <Button 
                      onClick={handleCancel}
                      variant="outline"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button 
                    onClick={() => setEditing(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Contact Information */}
          <Card className="border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium">{user.email}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Phone</p>
                  {editing ? (
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="font-medium">{user.phone}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Military Details */}
          <Card className="border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                Military Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Branch</p>
                  <p className="font-medium">{user.branch}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Rank</p>
                  <p className="font-medium">{user.rank}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Service Number</p>
                  <p className="font-medium">{user.service_number}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <p className="font-medium">{user.status}</p>
                </div>
              </div>
              
              {user.unit && (
                <div>
                  <p className="text-sm text-gray-600">Unit/Command</p>
                  <p className="font-medium">{user.unit}</p>
                </div>
              )}
              
              {user.clearance_level && (
                <div>
                  <p className="text-sm text-gray-600">Security Clearance</p>
                  <Badge variant="outline" className="border-yellow-400 text-yellow-700 bg-yellow-50">
                    <Shield className="w-3 h-3 mr-1" />
                    {user.clearance_level}
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Address Information */}
          <Card className="border-0 bg-white/80 backdrop-blur-sm lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-600" />
                Mailing Address
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Street Address
                    </label>
                    <input
                      type="text"
                      name="address.street"
                      value={formData.address.street}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <select
                      name="address.state"
                      value={formData.address.state}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select State</option>
                      {states.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      name="address.zip"
                      value={formData.address.zip}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              ) : (
                <div className="text-gray-700">
                  {user.address?.street && (
                    <>
                      <p>{user.address.street}</p>
                      <p>
                        {user.address.city}
                        {user.address.city && user.address.state && ', '}
                        {user.address.state} {user.address.zip}
                      </p>
                    </>
                  )}
                  {!user.address?.street && (
                    <p className="text-gray-500 italic">No address on file</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Account Information */}
          <Card className="border-0 bg-white/80 backdrop-blur-sm lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Account Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <Calendar className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Member Since</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(user.created_at).toLocaleDateString()}
                  </p>
                </div>
                
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <Award className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Profile Status</p>
                  <Badge className="bg-green-100 text-green-800">Verified</Badge>
                </div>
                
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <Shield className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Security Level</p>
                  <p className="font-semibold text-gray-900">High</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;